import { addToast as heroAddToast, ToastProvider as HeroToastProvider, ToastProps } from '@heroui/react';
import { CircleHelp, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

export const showToast = (
  props: Pick<
    ToastProps,
    | 'title'
    | 'icon'
    | 'description'
    | 'color'
    | 'endContent'
    | 'timeout'
    | 'hideIcon'
    | 'hideCloseButton'
    | 'shouldShowTimeoutProgress'
    | 'classNames'
    | 'placement'
  > & {
    color: Extract<ToastProps['color'], 'success' | 'danger' | 'secondary'>;
  },
) => {
  const toastProps: ToastProps = {
    ...(props as ToastProps),
    classNames: { ...(props as ToastProps).classNames },
  };
  toastProps.closeIcon = <X />;

  switch (props.color) {
    case 'success':
      toastProps.color = 'success';
      toastProps.classNames = {
        ...toastProps.classNames,
        content: twMerge('gap-x-3', toastProps.classNames?.content),
        closeButton: 'top-1/2 -translate-y-1/2 right-3 opacity-100',
        base: twMerge('border-success-300 shadow-xl rounded-xl cursor-default', toastProps.classNames?.base),
      };
      break;
    case 'danger':
      toastProps.color = 'danger';
      toastProps.classNames = {
        ...toastProps.classNames,
        content: twMerge('gap-x-3', toastProps.classNames?.content),
        closeButton: 'top-1/2 -translate-y-1/2 right-3 opacity-100',
        base: twMerge('border-danger-300 shadow-xl rounded-xl cursor-default', toastProps.classNames?.base),
      };
      break;
    case 'secondary':
      toastProps.color = 'secondary';
      toastProps.icon = props.icon ?? <CircleHelp color="black" />;
      toastProps.classNames = {
        ...toastProps.classNames,
        content: twMerge('gap-x-3', toastProps.classNames?.content),
        closeButton: 'top-1/2 -translate-y-1/2 right-3 opacity-100',
        icon: twMerge('text-secondary-50', toastProps.classNames?.icon),
        title: twMerge('text-secondary-50', toastProps.classNames?.title),
        description: twMerge('text-secondary-100', toastProps.classNames?.description),
        base: twMerge(
          'border-secondary-950 shadow-xl rounded-xl bg-secondary-950 cursor-default',
          // The close button is pulled leftwards out of the flow, so an action beside it needs
          // room to clear it.
          props.endContent != null && props.hideCloseButton !== true && 'pr-10',
          // Actions sit on the dark surface, where every Button colour reads as dark on dark. The
          // close button keeps the light text but neither the border nor the inverting fill, which
          // would draw a ring around its icon.
          '[&_button]:text-secondary-50',
          '[&_button:not([aria-label=closeButton])]:border-secondary-50/40',
          '[&_button:not([aria-label=closeButton]):hover]:bg-secondary-50',
          '[&_button:not([aria-label=closeButton]):hover]:text-secondary-950',
          toastProps.classNames?.base,
        ),
      };
      break;
  }

  return heroAddToast(toastProps);
};

export const ToastProvider = () => (
  <HeroToastProvider placement="bottom-center" regionProps={{ classNames: { base: 'z-[300]' } }} />
);
